/**
 * 2026-05-25 — consolidated "Details" section for the experimental
 * crypto-price-style market view. Replaces two stacked sections
 * (About + Resolution) with a single section, two labeled
 * subsections, and one shared "Show more / Show less" toggle. Both
 * subsections stay visible by default but truncate via a fade-to-
 * transparent mask; one click reveals the full body of both.
 *
 * UI rationale: tabs add a click and hide the resolution criteria
 * (highest-trust copy on the page); stacked + one shared toggle
 * keeps everything scannable while saving vertical space vs two
 * separate expand controls.
 */
"use client";

import { useState } from "react";

interface Props {
  description: string;
  resolvesAt: string;
  resolutionSource: string;
  viewerLocale: string;
}

const TOKENS = {
  text: "#0A0B0D",
  textSecondary: "#5B616E",
  border: "#ECEFF2",
};

const COLLAPSED_MAX = "120px";
const EXPANDED_MAX = "2000px";

export function MarketDetailsSection({
  description,
  resolvesAt,
  resolutionSource,
  viewerLocale,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const maskStyle = expanded
    ? {}
    : {
        WebkitMaskImage:
          "linear-gradient(to bottom, black 50%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, black 50%, transparent 100%)",
      };

  return (
    <section
      className="mt-10 border-t pt-8"
      style={{ borderColor: TOKENS.border }}
    >
      <h2
        className="mb-6 text-[20px] font-semibold tracking-[-0.01em]"
        style={{ color: TOKENS.text }}
      >
        Details
      </h2>

      {/* About subsection */}
      <div>
        <h3
          className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: TOKENS.text }}
        >
          About this market
        </h3>
        <div
          className="overflow-hidden transition-[max-height] duration-300 ease-out"
          style={{
            maxHeight: expanded ? EXPANDED_MAX : COLLAPSED_MAX,
            ...maskStyle,
          }}
        >
          <p
            className="whitespace-pre-line text-[15px] leading-[1.55]"
            style={{ color: TOKENS.text }}
          >
            {description}
          </p>
        </div>
      </div>

      {/* Resolution subsection */}
      <div className="mt-6">
        <h3
          className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: TOKENS.text }}
        >
          Resolution
        </h3>
        <div
          className="overflow-hidden transition-[max-height] duration-300 ease-out"
          style={{
            maxHeight: expanded ? EXPANDED_MAX : COLLAPSED_MAX,
            ...maskStyle,
          }}
        >
          <p
            className="text-[14px] leading-[1.55]"
            style={{ color: TOKENS.textSecondary }}
          >
            Resolves:{" "}
            <span style={{ color: TOKENS.text }}>
              {new Date(resolvesAt).toLocaleString(viewerLocale, {
                dateStyle: "long",
                timeStyle: "short",
              })}
            </span>
          </p>
          <p
            className="mt-2 whitespace-pre-line text-[14px] leading-[1.55]"
            style={{ color: TOKENS.text }}
          >
            {resolutionSource || "Granbay admin team"}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-4 text-[12px] font-semibold underline-offset-2 hover:underline"
        style={{ color: TOKENS.textSecondary }}
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </section>
  );
}
